import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import backIcon from '../../../images/icon-chevron-left.svg';
import { SupportTicketActionModal } from '../modals/SupportTicketActionModal';
import styles from './SupportTicketDetail.module.css';
import CustModal from '../../common/custmodal/CustModal';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/rootReducer';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../redux/rootReducer';
import { CreateSupportTicketComment, getSupportTicketDetail } from '../../../redux/support-ticket/actions/support-ticket.action';
import Loader from '../../common/loader/Loader';
import Toast from '../../common/toast/Toast';
import UserAvatar from '../../common/user/UserAvatar';
import { SupportTicketDetail as SupportTicketDetailInterface } from '../interface/Support.interface';
import { SupportTicketComment } from '../interface/Support.interface';

const SupportTicketDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [showReopenModal, setShowReopenModal] = useState(false);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const [searchText, setSearchText] = useState('');
    const ticket = useSelector(
        (state: IRootState) =>
            state.supportTicketReducer.ticketDetail as SupportTicketDetailInterface | null
    );
    const loading = useSelector((state: IRootState) => state.supportTicketReducer.loading);
    useEffect(() => {
        if (id) {
            dispatch(getSupportTicketDetail(id));
        }
    }, [id, dispatch]);

    if (loading) {
        return (
            <div className={`${styles.detailContainer} outletContainer`}>
                <Loader />
            </div>
        );
    }

    if (!ticket) {
        return (
            <div className={`${styles.detailContainer} outletContainer`}>
                <div className={styles.headerDeatils}>
                    <button className={styles.backBtn} onClick={() => navigate('/support-ticket')}>
                        <img src={backIcon} alt="back" width={16} height={16} />
                    </button>
                    <span className={styles.ticketId}>Ticket Not Found</span>
                </div>
                <div style={{ textAlign: 'center', marginTop: '50px' }}>
                    <p>Sorry, the ticket you are looking for does not exist or has been removed.</p>
                </div>
            </div>
        );
    }

    const performSearchAndFilter = (text: string) => {
        let result = [...ticket?.comments || []];

        if (text) {
            const lowerText = text.toLowerCase();
            result = result.filter((comment: SupportTicketComment) =>
                comment?.id?.toLowerCase().includes(lowerText) ||
                comment?.comment?.toLowerCase().includes(lowerText)
            );
        }

    };
    const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
        const { value } = e.target as HTMLInputElement;
        setSearchText(value);

        if (value === '') {
            performSearchAndFilter('');
        } else {
            performSearchAndFilter(value);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ' ' && String(searchText).length === 0) {
            event.preventDefault();
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (ticket?.ticketStatus === 'closed') return;
        if (!searchText.trim() || !ticket?.id) return;

        const commentText = searchText.trim();
        setSearchText('');
        dispatch(
            CreateSupportTicketComment(
                ticket.id,
                commentText,
                () => {
                    dispatch(getSupportTicketDetail(ticket.id, true));
                },
                (error) => {
                    setSearchText(commentText);
                }
            )
        );
    };

    // Last update: show only when ticket has been updated (comment added or status changed), not on first create
    const hasComments = (ticket?.comments || []).length > 0;
    const createdTime = ticket?.createdAt ? new Date(ticket.createdAt).getTime() : 0;
    const updatedTime = ticket?.updatedAt ? new Date(ticket.updatedAt).getTime() : 0;
    const ticketStatusOrOtherUpdate = updatedTime > 0 && updatedTime !== createdTime;
    const commentDates = (ticket?.comments || []).map((c: SupportTicketComment) =>
        c.createdAt ? new Date(c.createdAt).getTime() : 0
    );
    const latestCommentTime = commentDates.length ? Math.max(...commentDates) : 0;
    const lastUpdatedAt = (hasComments || ticketStatusOrOtherUpdate)
        ? new Date(Math.max(updatedTime, latestCommentTime))
        : null;

    return (
        <React.Fragment>

            <div className={`${styles.detailContainer} outletConainer`}>
                {loading && <Loader />}
                <div className={styles.headerDeatils}>
                    <button className={styles.backBtn} onClick={() => navigate('/support-ticket')}>
                        <img src={backIcon} alt="back" width={16} height={16} />
                    </button>
                    <span className={styles.ticketId}>{ticket.id}</span>
                </div>

                <div className={styles.contentWrapper}>
                    <div className={styles.mainContent}>
                        <div className={styles.mainContentWrapper}>
                            <h1 className={styles.subject}>{ticket.subject}</h1>
                            <p className={styles.description}>{ticket.description}</p>

                            <div className={styles.messageThread}>
                                {[...(ticket.comments || [])]
                                    .sort((a, b) => {
                                        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                                        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                                        return timeB - timeA; // descending: newest first
                                    })
                                    .map((msg) => (
                                        <div key={msg.id} className={styles.message}>

                                            {/* Avatar */}
                                            <div className={styles.avatarPlaceholder}>
                                                <UserAvatar
                                                    photoURL={msg.adminUser?.photo_url || ''}
                                                    displayOnly
                                                />
                                            </div>

                                            {/* Content */}
                                            <div className={styles.messageContent}>

                                                {/* Name */}
                                                <div className={styles.messageHeader}>
                                                    <span className={styles.authorName}>
                                                        {msg.adminUser?.fullName?.trim()}
                                                    </span>

                                                    {/* Date */}
                                                    <span className={styles.messageDate}>
                                                        {msg.createdAt
                                                            ? new Date(msg.createdAt).toLocaleString()
                                                            : ""}
                                                    </span>
                                                </div>

                                                {/* Comment */}
                                                <div className={styles.messageBody}>
                                                    {msg.comment}
                                                </div>

                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                        <div
                            className={`${styles.searchFilterContainer} ${ticket.ticketStatus === 'closed' ? styles.commentBoxDisabled : ''}`}
                        >
                            <form onSubmit={handleSubmit} className={styles.searchContainer}>
                                <div className={styles.searchBox}>
                                    <input
                                        onKeyPress={handleKeyPress}
                                        value={searchText}
                                        onChange={(e) => handleChange(e)}
                                        onPaste={(e: React.ClipboardEvent) => {
                                            if (ticket.ticketStatus === 'closed') {
                                                e.preventDefault();
                                                return;
                                            }
                                            e.preventDefault();
                                            const pastedValue = e?.clipboardData?.getData('text/plain');
                                            setSearchText(pastedValue?.trim() || '');
                                        }}
                                        type="text"
                                        placeholder={
                                            ticket.ticketStatus === 'closed'
                                                ? 'Ticket closed'
                                                : 'Type your message here...'
                                        }
                                        maxLength={50}
                                        disabled={ticket.ticketStatus === 'closed'}
                                        aria-disabled={ticket.ticketStatus === 'closed'}
                                    />
                                    <button
                                        type="submit"
                                        className={searchText !== '' ? 'active' : ''}
                                        disabled={searchText === '' || ticket.ticketStatus === 'closed'}
                                    >
                                        Send
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className={styles.sidebar}>
                        <div className={styles.sidebarWrap}>
                            <div className={styles.card}>
                                <h2 className={styles.cardTitle}>Tickets Information</h2>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Status</span>
                                    <span className={`${styles.statusBadge} ${ticket.ticketStatus === "closed"
                                        ? styles.statusResolved
                                        : ticket.ticketStatus === "re_open"
                                            ? styles.statusInProgress
                                            : styles.statusOpen
                                        }`}>
                                        {ticket.ticketStatus.replace("_", " ").toUpperCase()}
                                    </span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Created</span>
                                    <span className={styles.infoValue}>{new Date(ticket.createdAt).toLocaleString()}</span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Last Update</span>
                                    <span className={styles.infoValue}>
                                        {lastUpdatedAt ? lastUpdatedAt.toLocaleString() : "-"}
                                    </span>
                                </div>
                                {ticket.ticketStatus !== 'closed' && (
                                    <button className={`${styles.reopenBtn} ${styles.closeBtn}`} onClick={() => setShowCloseModal(true)}>Close Ticket</button>
                                )}
                            </div>
                            {ticket.ticketStatus === 'closed' && (
                                <div className={styles.reopenCard}>

                                    <>
                                        <h3 className={styles.reopenTitle}>Not satisfied with the resolution?</h3>
                                        <p className={styles.reopenText}>
                                            If the issue still persists or you're not fully satisfied with the resolution, you can reopen this ticket and share additional details with our support team.
                                        </p>
                                        <button className={styles.reopenBtn} onClick={() => setShowReopenModal(true)}>Reopen Ticket</button>
                                    </>

                                </div>
                            )}
                        </div>
                    </div>

                </div>
                {showToast && <Toast type={toastType} message={toastMessage} />}
            </div>
            <CustModal
                open={showCloseModal}
                setOpen={setShowCloseModal}
                bodyData={
                    <SupportTicketActionModal
                        title="Close Support Ticket"
                        buttonText="Close Ticket"
                        actionType="close"
                        ticketId={ticket.id}
                        onClose={() => setShowCloseModal(false)}
                        setShowToast={setShowToast}
                        setToastType={setToastType}
                        setToastMessage={setToastMessage}
                    />
                }
            />
            <CustModal
                open={showReopenModal}
                setOpen={setShowReopenModal}
                bodyData={
                    <SupportTicketActionModal
                        title="Reopen Support Ticket"
                        buttonText="Reopen Ticket"
                        actionType="reopen"
                        ticketId={ticket.id}
                        onClose={() => setShowReopenModal(false)}
                        setShowToast={setShowToast}
                        setToastType={setToastType}
                        setToastMessage={setToastMessage}
                    />
                }
            />
            {/* <CustModal
                open={showReopenModal}
                setOpen={setShowReopenModal}
                bodyData={
                    <ReopenSupportTicket onClose={() => setShowReopenModal(false)} />
                }
            />
            <CustModal
                open={showCloseModal}
                setOpen={setShowCloseModal}
                bodyData={
                    <CloseSupportTicket onClose={() => setShowCloseModal(false)} />
                }
            /> */}
        </React.Fragment>
    );
};

export default SupportTicketDetail;
